import { ServiceCategory } from "./directory";

/**
 * What a service is, from our own catalogue (ADR 0033). A business names and prices its services
 * as it likes; the type it picks is what search, the directory and its public addresses read. A
 * service without one still books, but is not listed. The value is the type's English slug, the
 * same in every language (`/search?service=manual-therapy`).
 *
 * Variants are the service's own fields, never new types: first or follow-up visit, online, for two
 * or a group (capacity), length. That keeps the catalogue short enough to choose from.
 */
export enum ServiceType {
  // One to one
  PERSONAL_TRAINING = "personal-training",
  SMALL_GROUP_TRAINING = "small-group-training",
  TRAINING_CONSULTATION = "training-consultation",
  STRENGTH_TRAINING = "strength-training",
  FUNCTIONAL_TRAINING = "functional-training",
  MOBILITY_TRAINING = "mobility-training",
  MEDICAL_TRAINING = "medical-training",
  CORRECTIVE_EXERCISE = "corrective-exercise",
  PREGNANCY_TRAINING = "pregnancy-training",
  SENIOR_TRAINING = "senior-training",
  YOUTH_TRAINING = "youth-training",
  EMS_TRAINING = "ems-training",
  ATHLETIC_PERFORMANCE = "athletic-performance",
  // Online
  ONLINE_COACHING = "online-coaching",
  TRAINING_PLAN = "training-plan",
  // Sports
  RUNNING_COACHING = "running-coaching",
  SWIMMING_COACHING = "swimming-coaching",
  CYCLING_COACHING = "cycling-coaching",
  TRIATHLON_COACHING = "triathlon-coaching",
  HYROX_TRAINING = "hyrox-training",
  BOXING = "boxing",
  MARTIAL_ARTS = "martial-arts",
  CROSSFIT = "crossfit",
  CALISTHENICS = "calisthenics",
  // Clubs and classes
  GYM_ACCESS = "gym-access",
  YOGA = "yoga",
  PILATES = "pilates",
  REFORMER_PILATES = "reformer-pilates",
  INDOOR_CYCLING = "indoor-cycling",
  GROUP_FITNESS = "group-fitness",
  DANCE_FITNESS = "dance-fitness",
  POLE_DANCE = "pole-dance",
  // Nutrition
  NUTRITION_CONSULTATION = "nutrition-consultation",
  MEAL_PLAN = "meal-plan",
  NUTRITION_COACHING = "nutrition-coaching",
  BODY_COMPOSITION_ANALYSIS = "body-composition-analysis",
  SPORTS_NUTRITION = "sports-nutrition",
  CLINICAL_NUTRITION = "clinical-nutrition",
  WEIGHT_MANAGEMENT = "weight-management",
  CHILD_NUTRITION = "child-nutrition",
  PREGNANCY_NUTRITION = "pregnancy-nutrition",
  PSYCHODIETETICS = "psychodietetics",
  // Physiotherapy and rehab
  PHYSIO_CONSULTATION = "physio-consultation",
  ORTHOPAEDIC_REHAB = "orthopaedic-rehab",
  POST_SURGERY_REHAB = "post-surgery-rehab",
  SPORTS_PHYSIO = "sports-physio",
  NEURO_REHAB = "neuro-rehab",
  PELVIC_FLOOR_PHYSIO = "pelvic-floor-physio",
  PREGNANCY_PHYSIO = "pregnancy-physio",
  CHILD_PHYSIO = "child-physio",
  JAW_PHYSIO = "jaw-physio",
  // Therapies
  MANUAL_THERAPY = "manual-therapy",
  SOFT_TISSUE_THERAPY = "soft-tissue-therapy",
  DRY_NEEDLING = "dry-needling",
  KINESIOTAPING = "kinesiotaping",
  SHOCKWAVE_THERAPY = "shockwave-therapy",
  PHYSICAL_MODALITIES = "physical-modalities",
  OSTEOPATHY = "osteopathy",
  CHIROPRACTIC = "chiropractic",
  CRANIOSACRAL_THERAPY = "craniosacral-therapy",
  // Massage
  SPORTS_MASSAGE = "sports-massage",
  THERAPEUTIC_MASSAGE = "therapeutic-massage",
  DEEP_TISSUE_MASSAGE = "deep-tissue-massage",
  RELAXATION_MASSAGE = "relaxation-massage",
  LYMPHATIC_DRAINAGE = "lymphatic-drainage",
  PRENATAL_MASSAGE = "prenatal-massage",
  CUPPING = "cupping",
}

/** A shelf of the catalogue, for the headings a long list is read by. */
export enum ServiceTypeGroup {
  ONE_TO_ONE = "one-to-one",
  ONLINE = "online",
  SPORTS = "sports",
  CLASSES = "classes",
  NUTRITION = "nutrition",
  REHAB = "rehab",
  THERAPIES = "therapies",
  MASSAGE = "massage",
}

/** Each shelf's category: the one every type on it gives a service (ADR 0030). */
export const SERVICE_TYPE_GROUP_CATEGORY: Record<ServiceTypeGroup, ServiceCategory> = {
  [ServiceTypeGroup.ONE_TO_ONE]: ServiceCategory.TRAINING,
  [ServiceTypeGroup.ONLINE]: ServiceCategory.TRAINING,
  [ServiceTypeGroup.SPORTS]: ServiceCategory.TRAINING,
  [ServiceTypeGroup.CLASSES]: ServiceCategory.TRAINING,
  [ServiceTypeGroup.NUTRITION]: ServiceCategory.DIET,
  [ServiceTypeGroup.REHAB]: ServiceCategory.PHYSIO,
  [ServiceTypeGroup.THERAPIES]: ServiceCategory.PHYSIO,
  [ServiceTypeGroup.MASSAGE]: ServiceCategory.PHYSIO,
};

const shelf = (group: ServiceTypeGroup, types: ServiceType[]) => types.map((type) => [type, group] as const);

/** Which shelf each type sits on, in the order a list shows them. */
export const SERVICE_TYPE_GROUP: Record<ServiceType, ServiceTypeGroup> = Object.fromEntries([
  ...shelf(ServiceTypeGroup.ONE_TO_ONE, [
    ServiceType.PERSONAL_TRAINING,
    ServiceType.SMALL_GROUP_TRAINING,
    ServiceType.TRAINING_CONSULTATION,
    ServiceType.STRENGTH_TRAINING,
    ServiceType.FUNCTIONAL_TRAINING,
    ServiceType.MOBILITY_TRAINING,
    ServiceType.MEDICAL_TRAINING,
    ServiceType.CORRECTIVE_EXERCISE,
    ServiceType.PREGNANCY_TRAINING,
    ServiceType.SENIOR_TRAINING,
    ServiceType.YOUTH_TRAINING,
    ServiceType.EMS_TRAINING,
    ServiceType.ATHLETIC_PERFORMANCE,
  ]),
  ...shelf(ServiceTypeGroup.ONLINE, [ServiceType.ONLINE_COACHING, ServiceType.TRAINING_PLAN]),
  ...shelf(ServiceTypeGroup.SPORTS, [
    ServiceType.RUNNING_COACHING,
    ServiceType.SWIMMING_COACHING,
    ServiceType.CYCLING_COACHING,
    ServiceType.TRIATHLON_COACHING,
    ServiceType.HYROX_TRAINING,
    ServiceType.BOXING,
    ServiceType.MARTIAL_ARTS,
    ServiceType.CROSSFIT,
    ServiceType.CALISTHENICS,
  ]),
  ...shelf(ServiceTypeGroup.CLASSES, [
    ServiceType.GYM_ACCESS,
    ServiceType.YOGA,
    ServiceType.PILATES,
    ServiceType.REFORMER_PILATES,
    ServiceType.INDOOR_CYCLING,
    ServiceType.GROUP_FITNESS,
    ServiceType.DANCE_FITNESS,
    ServiceType.POLE_DANCE,
  ]),
  ...shelf(ServiceTypeGroup.NUTRITION, [
    ServiceType.NUTRITION_CONSULTATION,
    ServiceType.MEAL_PLAN,
    ServiceType.NUTRITION_COACHING,
    ServiceType.BODY_COMPOSITION_ANALYSIS,
    ServiceType.SPORTS_NUTRITION,
    ServiceType.CLINICAL_NUTRITION,
    ServiceType.WEIGHT_MANAGEMENT,
    ServiceType.CHILD_NUTRITION,
    ServiceType.PREGNANCY_NUTRITION,
    ServiceType.PSYCHODIETETICS,
  ]),
  ...shelf(ServiceTypeGroup.REHAB, [
    ServiceType.PHYSIO_CONSULTATION,
    ServiceType.ORTHOPAEDIC_REHAB,
    ServiceType.POST_SURGERY_REHAB,
    ServiceType.SPORTS_PHYSIO,
    ServiceType.NEURO_REHAB,
    ServiceType.PELVIC_FLOOR_PHYSIO,
    ServiceType.PREGNANCY_PHYSIO,
    ServiceType.CHILD_PHYSIO,
    ServiceType.JAW_PHYSIO,
  ]),
  ...shelf(ServiceTypeGroup.THERAPIES, [
    ServiceType.MANUAL_THERAPY,
    ServiceType.SOFT_TISSUE_THERAPY,
    ServiceType.DRY_NEEDLING,
    ServiceType.KINESIOTAPING,
    ServiceType.SHOCKWAVE_THERAPY,
    ServiceType.PHYSICAL_MODALITIES,
    ServiceType.OSTEOPATHY,
    ServiceType.CHIROPRACTIC,
    ServiceType.CRANIOSACRAL_THERAPY,
  ]),
  ...shelf(ServiceTypeGroup.MASSAGE, [
    ServiceType.SPORTS_MASSAGE,
    ServiceType.THERAPEUTIC_MASSAGE,
    ServiceType.DEEP_TISSUE_MASSAGE,
    ServiceType.RELAXATION_MASSAGE,
    ServiceType.LYMPHATIC_DRAINAGE,
    ServiceType.PRENATAL_MASSAGE,
    ServiceType.CUPPING,
  ]),
]) as Record<ServiceType, ServiceTypeGroup>;

/** Every type in catalogue order: shelf by shelf, as the enum lists them. */
export const SERVICE_TYPES = Object.values(ServiceType);

/** The category a type gives the service that picks it, and so the profession of who delivers it. */
export const serviceTypeCategory = (type: ServiceType): ServiceCategory =>
  SERVICE_TYPE_GROUP_CATEGORY[SERVICE_TYPE_GROUP[type]];

/** One type as an app describes it in the reader's language, for `searchServiceTypes`. */
export type ServiceTypeEntry = {
  type: ServiceType;
  name: string;
  /** Other words people search it by: synonyms, the profession, goals, the body part or complaint. */
  terms: string[];
};

export type ServiceTypeMatch = {
  type: ServiceType;
  /** The search term it matched on, when that was not the name: shown as "for back pain". */
  term: string | null;
  score: number;
};

/** Lower case, no diacritics, words only: "Ból pleców" and "bol plecow" are the same words. */
export const foldSearchText = (text: string) =>
  text
    .normalize("NFKD")
    .replaceAll(/\p{M}/gu, "")
    .toLowerCase()
    .replaceAll("ł", "l")
    .replaceAll(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/** Edits between two words, a swap of neighbours counting as one ("kinesoitaping"). */
const editDistance = (a: string, b: string) => {
  const rows = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array<number>(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) rows[0]![j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(rows[i - 1]![j]! + 1, rows[i]![j - 1]! + 1, rows[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
        best = Math.min(best, rows[i - 2]![j - 2]! + 1);
      rows[i]![j] = best;
    }
  }
  return rows[a.length]![b.length]!;
};

/**
 * How well one typed word fits one word of a phrase, from 0 (not at all) to 1 (its start, as
 * typed). A word still being typed matches the start of the longer one; past three letters a
 * slip is forgiven (one for a short word, two for a long one), but never so loosely that "joga"
 * finds "depilacja".
 */
const wordFit = (typed: string, word: string) => {
  if (word.startsWith(typed)) return 1;
  if (typed.length < 4) return 0;
  const allowed = typed.length >= 7 ? 2 : 1;
  // Against the same length of the word, so an unfinished word is not punished for what is missing.
  const distance = Math.min(editDistance(typed, word.slice(0, typed.length)), editDistance(typed, word));
  return distance <= allowed ? 0.7 - distance * 0.1 : 0;
};

/**
 * How well the typed words fit a phrase. Each must fit one of its words, or the phrase is out;
 * `loose`, a word that fits nothing is passed over ("60 min" in a service's name) and only one has
 * to fit, at a score that shrinks with each word left over.
 */
const phraseFit = (typedWords: string[], phrase: string, loose: boolean) => {
  const words = phrase.split(" ");
  let total = 0;
  let fitted = 0;
  for (const typed of typedWords) {
    const best = Math.max(0, ...words.map((word) => wordFit(typed, word)));
    if (best === 0 && !loose) return 0;
    if (best > 0) fitted += 1;
    total += best;
  }
  if (fitted === 0) return 0;
  // A phrase that starts with the query beats one that only contains it.
  return total / typedWords.length + (phrase.startsWith(typedWords[0] ?? "") ? 0.1 : 0);
};

/**
 * The catalogue types that fit what someone typed, best first. The name counts more than another
 * term, so "masaż" lists the massages before a physio service someone searches by "masaż". Works
 * on what the app hands it, in its own language, so the catalogue never travels as copy.
 */
export const searchServiceTypes = ({
  query,
  entries,
  limit = 8,
  loose = false,
}: {
  query: string;
  entries: ServiceTypeEntry[];
  limit?: number;
  /** Words that fit nothing are passed over: for reading a service's own name, not a search. */
  loose?: boolean;
}): ServiceTypeMatch[] => {
  const typedWords = foldSearchText(query).split(" ").filter(Boolean);
  if (typedWords.length === 0) return [];

  return entries
    .flatMap((entry): ServiceTypeMatch[] => {
      const byName = phraseFit(typedWords, foldSearchText(entry.name), loose);
      let best: ServiceTypeMatch | null = byName > 0 ? { type: entry.type, term: null, score: byName + 0.5 } : null;
      for (const [index, term] of entry.terms.entries()) {
        // Terms are listed most telling first: "kolano" early in rehab outranks it late in training.
        const fit = phraseFit(typedWords, foldSearchText(term), loose);
        const score = fit - index * 0.002;
        if (fit > 0 && (!best || score > best.score)) best = { type: entry.type, term, score };
      }
      return best ? [best] : [];
    })
    .toSorted((a, b) => b.score - a.score)
    .slice(0, limit);
};
